import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import styles from './styles/App.module.css';
import Login from './components/Login';
import Landing from './components/Landing';
import NotFoundPage from './components/NotFoundPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthView, PublicRoute } from './types/enums';
import { ErrorBoundary } from './components/shared/ErrorBoundary';

// Lazy-load the entire authenticated app — keeps the public landing/login
// bundle tiny (so visitors who never sign in don't pay for the whole doctor
// app's JS + CSS).
const DoctorAppShell = lazy(() => import('./AppShell'));
const PatientAppShell = lazy(() => import('./PatientAppShell'));

/**
 * Resolves the current public (unauthenticated) route from window.location.
 * Anything outside the known public routes returns NOT_FOUND so the router
 * can render the 404 page (audit B503). Previously unknown paths silently
 * rendered the marketing landing page with no signal that the user's
 * requested URL was wrong.
 */
const resolvePublicRoute = (): PublicRoute => {
  if (typeof window === 'undefined') return PublicRoute.LANDING;
  const path = window.location.pathname;
  if (path === PublicRoute.LANDING) return PublicRoute.LANDING;
  if (path === PublicRoute.LOGIN) return PublicRoute.LOGIN;
  if (path === PublicRoute.SIGNUP) return PublicRoute.SIGNUP;
  if (path === PublicRoute.DOCTOR_SIGNUP) return PublicRoute.DOCTOR_SIGNUP;
  if (path === PublicRoute.PATIENT_SIGNUP) return PublicRoute.PATIENT_SIGNUP;
  return PublicRoute.NOT_FOUND;
};

/**
 * Public (unauthenticated) shell — handles routing between Landing and Login
 * via the History API.
 */
const PublicShell: React.FC = () => {
  const [route, setRoute] = useState<PublicRoute>(resolvePublicRoute);

  useEffect(() => {
    const onPopState = () => setRoute(resolvePublicRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: PublicRoute) => {
    // NOT_FOUND is a computed state, not a destination — never write
    // its sentinel string to the URL.
    if (next === PublicRoute.NOT_FOUND) {
      setRoute(next);
      return;
    }
    if (typeof window !== 'undefined' && window.location.pathname !== next) {
      window.history.pushState({}, '', next);
    }
    setRoute(next);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const goToLogin = useCallback(() => navigate(PublicRoute.LOGIN), [navigate]);
  const goToDoctorSignup = useCallback(() => navigate(PublicRoute.DOCTOR_SIGNUP), [navigate]);
  const goToPatientSignup = useCallback(() => navigate(PublicRoute.PATIENT_SIGNUP), [navigate]);
  const goToLanding = useCallback(() => navigate(PublicRoute.LANDING), [navigate]);

  if (route === PublicRoute.LOGIN) {
    return <Login initialView={AuthView.SIGN_IN} onBackToLanding={goToLanding} />;
  }
  if (route === PublicRoute.SIGNUP || route === PublicRoute.DOCTOR_SIGNUP) {
    return <Login initialView={AuthView.REGISTER} onBackToLanding={goToLanding} intentRole="DOCTOR" />;
  }
  if (route === PublicRoute.PATIENT_SIGNUP) {
    return <Login initialView={AuthView.REGISTER} onBackToLanding={goToLanding} intentRole="PATIENT" />;
  }
  if (route === PublicRoute.NOT_FOUND) {
    return <NotFoundPage onGoHome={goToLanding} onSignIn={goToLogin} />;
  }
  return (
    <Landing
      onSignIn={goToLogin}
      onGetStarted={goToDoctorSignup}
      onPatientSignup={goToPatientSignup}
    />
  );
};

/**
 * Suspense fallback shown while the authenticated AppShell chunk downloads.
 * Kept inline (no separate component file) and minimal so it loads instantly.
 */
const AppLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--color-background, #f7fafc)',
      color: 'var(--color-text-secondary, #4a5568)',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.9375rem',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
      <div
        style={{
          width: 16,
          height: 16,
          border: '2px solid var(--color-border, #e2e8f0)',
          borderTopColor: 'var(--color-primary, #2A64F5)',
          borderRadius: '50%',
          animation: 'docx-spin 0.8s linear infinite',
        }}
      />
      <span>Loading…</span>
    </div>
    <style>{`@keyframes docx-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/**
 * Decides which surface to render based on auth state.
 * Public surface is in the main bundle; AppShell is lazy-loaded.
 */
const AppRoot: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return (
      <div className={styles.App}>
        <ErrorBoundary>
          <PublicShell />
        </ErrorBoundary>
      </div>
    );
  }
  return (
    <ErrorBoundary>
      <Suspense fallback={<AppLoader />}>
        {user?.role === 'PATIENT' ? <PatientAppShell /> : <DoctorAppShell />}
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * Root App component wrapped with AuthProvider
 */
const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <ThemeProvider>
        <AuthProvider>
          <AppRoot />
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
